import React, { useEffect } from 'react';

import {
  IReportFilterDtoPlanYearPeriodEnum,
} from '@/dtos';
import { useFilters } from '@/utils';

import { useFavoriteReportHandler } from '../../hooks';
import { ReportDetailsProps, ReportTab, ReportTypeKeys } from '../../report.types';
import { useGetReportEmployerId, useMultipleEmployerSelectAvailable } from '../hooks';
import ReportDetailsBase from '../ReportDetailsBase';

import {
  HealthPlanEnrollmentsReportParams,
  HealthPlanEnrollmentsReportTableParams,
} from './HealthPlanEnrollmentReport.types';
import HealthPlanEnrollmentsReportSummary from './HealthPlanEnrollmentReportSummary';
import HealthPlanEnrollmentsReportTable from './HealthPlanEnrollmentReportTable';

type FiltersValues = HealthPlanEnrollmentsReportTableParams &
  HealthPlanEnrollmentsReportParams & {
    tab: ReportTab;
  };
interface Props extends ReportDetailsProps<FiltersValues> {}
const HealthPlanEnrollmentsReportDetails: React.FC<Props> = ({
  defaultEmployerId,
  defaultTab,
  isPreviewMode,
  onChangeFilters,
  defaultFilterValues,
}) => {
  const employerId = useGetReportEmployerId();

  const { filtersValues, setFilterValue } = useFilters<FiltersValues>(
    {
      tab: defaultTab || ReportTab.Summary,
      employerId: defaultEmployerId || employerId || '',
      employerIds: defaultEmployerId || employerId ? [employerId] : [],
      planYearPeriod: [],
      searchString: '',
      isSSNShowed: false,
      reportPlanIds: [],
      ...defaultFilterValues,
    },
    {
      disabled: isPreviewMode,
      autoClear: true,
    }
  );

  useEffect(() => {
    if (isPreviewMode) {
      return;
    }
    setFilterValue.reportPlanIds([]);
    if (isMultipleEmployerAvailable) {
      setFilterValue.planYearPeriod([IReportFilterDtoPlanYearPeriodEnum.CURRENT]);
    } else {
      setFilterValue.planYearPeriod([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersValues.employerId, filtersValues.employerIds?.join(',')]);
  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();

  useEffect(() => {
    if (isMultipleEmployerAvailable) {
      setFilterValue.planYearPeriod([IReportFilterDtoPlanYearPeriodEnum.CURRENT]);
    } else {
      setFilterValue.planYearPeriod([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultipleEmployerAvailable]);
  useEffect(() => {
    if (employerId && !filtersValues.employerId) {
      setFilterValue.employerId(employerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employerId]);
  useEffect(() => {
    if (defaultTab) {
      setFilterValue.tab(defaultTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab]);
  useEffect(() => {
    if (isPreviewMode && onChangeFilters) {
      onChangeFilters(filtersValues);
    }
  }, [filtersValues, isPreviewMode, onChangeFilters]);
  const { isFavorite, handleToggleReportKey } = useFavoriteReportHandler(ReportTypeKeys.HealthPlanEnrollment);
  return (
    <ReportDetailsBase
      isPreviewMode={isPreviewMode}
      isFavorite={isFavorite}
      onToggleFavorite={handleToggleReportKey}
      tab={filtersValues.tab}
      onSetTab={setFilterValue.tab}
      title="Health Plan Enrollment"
    >
      {filtersValues.tab === ReportTab.Report ? (
        <HealthPlanEnrollmentsReportTable
          defaultEmployerId={defaultEmployerId}
          isPreviewMode={isPreviewMode}
          key={filtersValues.tab}
          filtersValues={filtersValues}
          onSetFilterValue={setFilterValue}
        />
      ) : (
        <HealthPlanEnrollmentsReportSummary
          defaultEmployerId={defaultEmployerId}
          isPreviewMode={isPreviewMode}
          filtersValues={filtersValues}
          onSetFilterValue={setFilterValue}
        />
      )}
    </ReportDetailsBase>
  );
};

export default HealthPlanEnrollmentsReportDetails;
